import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ACCOUNTING_API, EINVOICE_API } from '../../../config/api';
import { accountingApi, einvoiceApi } from '../../../config/apiClient';
import { useTranslation } from 'react-i18next';

interface InvoiceResponseModalProps {
  open: boolean;
  onClose: () => void;
  ettn: string;
  belgeNo: string;
  responseType: 'KABUL' | 'RED';
  companyId: string;
  /** Yanıt başarıyla gönderildiğinde tetiklenir (liste durumunu anında güncellemek için). */
  onSuccess?: (responseType: 'KABUL' | 'RED', ettn: string) => void;
}

interface LogEntry {
  timestamp: string;
  type: 'info' | 'success' | 'error' | 'warning';
  message: string;
}

const InvoiceResponseModal: React.FC<InvoiceResponseModalProps> = ({
  open,
  onClose,
  ettn,
  belgeNo,
  responseType,
  companyId,
  onSuccess
}) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const [redNedeni, setRedNedeni] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isCompleted, setIsCompleted] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [logsExpanded, setLogsExpanded] = useState(false);
  const [gonderilenXml, setGonderilenXml] = useState<string>('');
  const logContainerRef = useRef<HTMLDivElement>(null);

  // Log ekle
  const addLog = (type: LogEntry['type'], message: string) => {
    const timestamp = new Date().toLocaleTimeString(localeCode, { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit',
      fractionalSecondDigits: 3
    });
    setLogs(prev => [...prev, { timestamp, type, message }]);
  };

  // Auto scroll to bottom when new log added
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs]);

  // Modal açıldığında reset
  useEffect(() => {
    if (open) {
      setLogs([]);
      setRedNedeni('');
      setIsProcessing(false);
      setIsCompleted(false);
      setHasError(false);
      setLogsExpanded(false);
      setGonderilenXml('');
      addLog('info', `📋 ${responseType === 'KABUL' ? tt('Kabul', 'Accept') : tt('Red', 'Reject')} ${tt('işlemi başlatılıyor...', 'operation is starting...')}`);
      addLog('info', `📄 ETTN: ${ettn}`);
      addLog('info', `🔖 ${tt('Belge No', 'Document No')}: ${belgeNo}`);
    }
  }, [open, ettn, belgeNo, responseType]);

  // Gönder butonu handler
  const handleSend = async () => {
    // Red için nedeni kontrol et
    if (responseType === 'RED' && !redNedeni.trim()) {
      addLog('error', tt('❌ Red nedeni zorunludur!', '❌ Rejection reason is required!'));
      return;
    }

    setIsProcessing(true);
    setHasError(false);

    try {
      addLog('info', tt('🔄 İşlem başlatılıyor...', '🔄 Operation starting...'));
      
      if (responseType === 'RED') {
        addLog('info', `📝 ${tt('Red Nedeni', 'Rejection Reason')}: ${redNedeni}`);
      }

      // 1. Entegrasyon bilgilerini SQL'den çek
      addLog('info', tt('🔐 Entegrasyon bilgileri yükleniyor...', '🔐 Loading integration information...'));
      const integrationResponse = await accountingApi.get(ACCOUNTING_API.EINVOICE_INTEGRATION_GET);
      const integrationData = integrationResponse.data;
      const integration = integrationData.data || integrationData;
      
      addLog('success', tt('✅ Entegrasyon bilgileri yüklendi', '✅ Integration information loaded'));
      addLog('info', `🏢 VKN: ${integration.vkn || integration.taxNumber}`);
      const providerType = (integration.providerType || integration.ProviderType || 'finansbank').toString().toLowerCase();
      addLog('info', `🔌 ${tt('Entegratör', 'Integrator')}: ${providerType === 'finansbank' ? 'QNB Finansbank' : 'EDM'}`);

      // ✅ EDM entegrasyonu → kabul/ret yanıtı EDM SOAP üzerinden (SendInvoiceResponseWithServerSign)
      if (providerType === 'edm') {
        addLog('info', tt('📤 EDM\'e yanıt gönderiliyor (sunucu imzalı)...', '📤 Sending response to EDM (server-signed)...'));
        const edmResp = await einvoiceApi.post(EINVOICE_API.EDM_INBOX_RESPOND, {
          uuid: ettn || null,
          id: belgeNo || null,
          accept: responseType === 'KABUL',
          description: responseType === 'RED' ? redNedeni : null,
          branchId: null,
        });
        const edmData = edmResp.data;
        if (!edmData?.success) {
          addLog('error', `❌ ${tt('EDM hatası', 'EDM error')}: ${edmData?.errorLong || edmData?.error || tt('Bilinmeyen hata', 'Unknown error')}`);
          throw new Error(edmData?.error || tt('EDM yanıtı gönderilemedi', 'Failed to send EDM response'));
        }
        addLog('success', `✅ ${responseType === 'KABUL' ? tt('Kabul', 'Accept') : tt('Red', 'Reject')} ${tt('yanıtı EDM üzerinden gönderildi', 'response sent via EDM')}`);
        if (edmData.returnCode) addLog('info', `📄 Return Code: ${edmData.returnCode}`);
        if (edmData.intlTxnId) addLog('info', `📄 Intl Txn ID: ${edmData.intlTxnId}`);
        addLog('success', tt('🎉 İşlem başarıyla tamamlandı!', '🎉 Operation completed successfully!'));
        addLog('info', tt('⏳ Durumun GİB\'e işlenmesi birkaç dakika sürebilir.', '⏳ It may take a few minutes for the status to reflect at the tax authority.'));
        setIsCompleted(true);
        setIsProcessing(false);
        onSuccess?.(responseType, ettn);
        return;
      }

      // 2. Backend e yanit gonder (QNB Finansbank)
      addLog('info', tt('📤 Yanıt XML\'i oluşturuluyor ve gönderiliyor...', '📤 Creating and sending response XML...'));
      
      const queryParams = new URLSearchParams({
        username: integration.username,
        password: integration.password,
        vkn: integration.vkn || integration.taxNumber,
        ettn: ettn,
        yanitTipi: responseType,
        providerType: integration.providerType || 'finansbank', // 'finansbank' veya 'edm'
      });

      if (responseType === 'RED') {
        queryParams.append('redNedeni', redNedeni);
      }

      // 3. Firebase'den firma detay bilgilerini çek
      addLog('info', tt('🏢 Firma detay bilgileri yükleniyor...', '🏢 Loading company details...'));
      
      let companyDetails: any = {};
      try {
        const { getCompanyInfo } = await import('../../../services/migration/companyDataService');
        companyDetails = (await getCompanyInfo(companyId)) || {};
        addLog('success', tt('✅ Firma detayları yüklendi', '✅ Company details loaded'));
      } catch (err) {
        addLog('warning', tt('⚠️ Firma detayları yüklenemedi, varsayılan değerler kullanılacak', '⚠️ Company details could not be loaded, default values will be used'));
        console.error('Firma detayları yüklenirken hata:', err);
      }

      // Firma bilgilerini query params'a ekle
      if (integration.companyName) {
        queryParams.append('bizimUnvan', integration.companyName);
      }
      if (companyDetails.taxOffice) {
        queryParams.append('bizimVergiDairesi', companyDetails.taxOffice);
      }
      if (companyDetails.address) {
        queryParams.append('bizimAdres', companyDetails.address);
      }
      if (companyDetails.phone) {
        queryParams.append('bizimTelefon', companyDetails.phone);
      }
      if (companyDetails.email) {
        queryParams.append('bizimEmail', companyDetails.email);
      }
      if (companyDetails.website) {
        queryParams.append('bizimWebsite', companyDetails.website);
      }

      const responseUrl = `${EINVOICE_API.QNB_GELEN_FATURA_YANIT_GONDER}?${queryParams.toString()}`;
      
      addLog('info', tt('🌐 API çağrısı yapılıyor: /GelenFaturaYanitGonder', '🌐 Calling API: /GelenFaturaYanitGonder'));

      const apiResponse = await fetch(responseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const responseText = await apiResponse.text();
      let responseData;
      
      try {
        responseData = JSON.parse(responseText);
      } catch {
        responseData = { message: responseText };
      }

      if (!apiResponse.ok) {
        addLog('error', `❌ ${tt('API hatası', 'API error')} (${apiResponse.status}): ${responseData.error || responseData.message || tt('Bilinmeyen hata', 'Unknown error')}`);
        if (responseData.details) {
          addLog('error', `📋 ${tt('Detaylar', 'Details')}: ${responseData.details}`);
        }
        throw new Error(responseData.error || responseData.message || tt('API hatası', 'API error'));
      }

      // Basari loglari
      addLog('success', tt('✅ Yanıt XML\'i başarıyla oluşturuldu', '✅ Response XML created successfully'));
      addLog('success', `✅ ${responseType === 'KABUL' ? tt('Kabul', 'Accept') : tt('Red', 'Reject')} ${tt('yanıtı QNB eFinans\'a gönderildi', 'response sent to QNB eFinans')}`);
      
      if (responseData.belgeOid) {
        addLog('info', `📄 Belge OID: ${responseData.belgeOid}`);
      }
      
      // XML'i state'e kaydet
      if (responseData.gonderilenXml) {
        setGonderilenXml(responseData.gonderilenXml);
        addLog('info', tt('📄 Gönderilen XML aşağıda gösterilmektedir.', '📄 Sent XML is shown below.'));
      }
      
      addLog('success', tt('🎉 İşlem başarıyla tamamlandı!', '🎉 Operation completed successfully!'));
      addLog('info', tt('⏳ Lütfen 10-15 saniye bekleyin, ardından tekrar durum sorgulayın.', '⏳ Please wait 10-15 seconds, then query the status again.'));
      addLog('info', tt('💡 QNB sistemi yanıtı işlemesi biraz zaman alabilir.', '💡 QNB system may take some time to process the response.'));
      addLog('info', tt('📋 Modal\'ı kapatmak için "Kapat" butonuna tıklayın.', '📋 Click "Close" to close this modal.'));
      
      setIsCompleted(true);
      onSuccess?.(responseType, ettn);

      // Modal otomatik kapanmasın - kullanıcı logları görmeli
      // setTimeout(() => {
      //   onClose();
      // }, 3000);

    } catch (error: any) {
      addLog('error', `❌ ${tt('Hata oluştu', 'An error occurred')}: ${error.message || tt('Bilinmeyen hata', 'Unknown error')}`);
      setHasError(true);
      setLogsExpanded(true); // Hata olduğunda logları otomatik aç
    } finally {
      setIsProcessing(false);
    }
  };

  // Log renk kodları
  const getLogColor = (type: LogEntry['type']) => {
    switch (type) {
      case 'success': return '#4caf50';
      case 'error': return '#f44336';
      case 'warning': return '#ff9800';
      case 'info': return '#2196f3';
      default: return '#757575';
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={!isProcessing ? onClose : undefined}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={isProcessing}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        {responseType === 'KABUL' ? (
          <>
            <CheckCircleIcon color="success" />
            <Typography variant="h6">{tt('✅ Fatura Kabul Et', '✅ Accept Invoice')}</Typography>
          </>
        ) : (
          <>
            <CancelIcon color="error" />
            <Typography variant="h6">{tt('🚫 Fatura Reddet', '🚫 Reject Invoice')}</Typography>
          </>
        )}
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {/* Fatura Bilgileri */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            📋 {tt('Fatura Bilgileri', 'Invoice Information')}
          </Typography>
          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
            <Typography variant="body2">
              <strong>ETTN:</strong> {ettn}
            </Typography>
            <Typography variant="body2">
              <strong>{tt('Belge No', 'Document No')}:</strong> {belgeNo}
            </Typography>
          </Paper>
        </Box>

        {/* Red Nedeni (sadece RED için) */}
        {responseType === 'RED' && (
          <Box sx={{ mb: 2 }}>
            <TextField
              label={tt('Red Nedeni *', 'Rejection Reason *')}
              multiline
              rows={3}
              fullWidth
              value={redNedeni}
              onChange={(e) => setRedNedeni(e.target.value)}
              disabled={isProcessing || isCompleted}
              placeholder={tt('Fatura satış anlaşmasına uygun fiyatlandırılmaması nedeniyle reddedilmiştir...', 'Invoice is rejected due to pricing not matching the sales agreement...')}
              helperText={tt('Faturayı neden reddettiğinizi açıklayın', 'Explain why you are rejecting the invoice')}
              error={!redNedeni.trim() && logs.some(l => l.message.includes('Red nedeni zorunlu'))}
            />
          </Box>
        )}

        {/* Log Alanı (varsayılan kapalı accordion) */}
        <Accordion
          expanded={logsExpanded}
          onChange={() => setLogsExpanded((prev) => !prev)}
          disableGutters
          variant="outlined"
          sx={{ mb: 2, '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2" color="text.secondary">
              📊 {tt('İşlem Logları', 'Operation Logs')}{logs.length > 0 ? ` (${logs.length})` : ''}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <Paper
              ref={logContainerRef}
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: '#1e1e1e',
                color: '#fff',
                maxHeight: 250,
                overflowY: 'auto',
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                border: 'none',
                borderRadius: 0,
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#555',
                  borderRadius: '4px',
                },
              }}
            >
              {logs.length === 0 ? (
                <Typography variant="body2" sx={{ color: '#888' }}>
                  {tt('Henüz log kaydı yok...', 'No logs yet...')}
                </Typography>
              ) : (
                logs.map((log, index) => (
                  <Box key={index} sx={{ mb: 0.5, display: 'flex', gap: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#888',
                        minWidth: '80px',
                        fontFamily: 'monospace'
                      }}
                    >
                      [{log.timestamp}]
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: getLogColor(log.type),
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}
                    >
                      {log.message}
                    </Typography>
                  </Box>
                ))
              )}
            </Paper>
          </AccordionDetails>
        </Accordion>

        {/* Gönderilen XML */}
        {gonderilenXml && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              📄 {tt('Gönderilen XML (Tam Hali)', 'Sent XML (Full Content)')}
            </Typography>
            <Paper 
              variant="outlined"
              sx={{ 
                p: 2, 
                bgcolor: '#f5f5f5', 
                maxHeight: 300,
                overflowY: 'auto',
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#bbb',
                  borderRadius: '4px',
                },
              }}
            >
              {gonderilenXml}
            </Paper>
          </Box>
        )}

        {/* Başarı/Hata Mesajı */}
        {isCompleted && !hasError && (
          <Alert severity="success" icon={<CheckCircleIcon />}>
            ✅ {responseType === 'KABUL' ? tt('Kabul', 'Accept') : tt('Red', 'Reject')} {tt('yanıtı başarıyla gönderildi!', 'response sent successfully!')}
          </Alert>
        )}
        {hasError && (
          <Alert severity="error" icon={<CancelIcon />}>
            ❌ {tt('İşlem sırasında bir hata oluştu. Lütfen logları kontrol edin.', 'An error occurred during the operation. Please check the logs.')}
          </Alert>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={isProcessing}
          startIcon={<CloseIcon />}
          variant={isCompleted || hasError ? 'contained' : 'outlined'}
          color={isCompleted && !hasError ? 'success' : 'inherit'}
        >
          {isCompleted || hasError ? tt('Kapat', 'Close') : tt('İptal', 'Cancel')}
        </Button>
        
        {!isCompleted && !hasError && (
          <Button
            variant="contained"
            color={responseType === 'KABUL' ? 'success' : 'error'}
            onClick={handleSend}
            disabled={isProcessing}
            startIcon={isProcessing ? <CircularProgress size={16} /> : <SendIcon />}
          >
            {isProcessing ? tt('Gönderiliyor...', 'Sending...') : (responseType === 'KABUL' ? tt('Kabul Et ve Gönder', 'Accept and Send') : tt('Reddet ve Gönder', 'Reject and Send'))}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceResponseModal;
